import React, { useCallback, useEffect, useMemo, useState } from "react";
import ContentHistoryPanel from "../Components/ContentHistory/ContentHistoryPanel";
import { Link } from "react-router-dom";
import { ContentDocument, ContentViewModel } from "../API/Models";
import type { SiteSettings as SiteSettingsModel } from "../types/layout";
import { client } from "../Clients";
import DeploymentStatusPanel from "../Components/DeploymentStatusPanel";
import UploadJsonModal from "../Components/UploadJsonModal";
import LayoutPreviewStrip from "../Components/LayoutEditor/LayoutPreviewStrip";
import { buildHeaderData } from "../Components/LayoutEditor/previewUtils";
import { buildLayoutPayloadFromSettings, sanitizeLayoutSettings as sanitizeLayoutSettingsLayout } from "../Components/LayoutEditor/utils";
import { usePageHeader } from "../hooks/usePageHeader";
import { FiUpload, FiEye } from "react-icons/fi";
import LayoutPreviewModal from "../Components/LayoutEditor/LayoutPreviewModal";
import { showSuccessModal } from "../Helper/showSuccessModal";
import type { LanguageItem, LanguagesContent } from "./languageUtils";
import {
	createEmptyLanguage,
	buildComparableFromContent,
	areComparablesEqual,
	sanitizeContent
} from "./languageUtils";

type DeploymentStatus = {
	state: "up-to-date" | "needs-deploy" | "pending";
	deployedAt: string | null;
};

const CONTENT_TYPE_KEY = "languages";
const CONTENT_LANGUAGE = "default";
const FALLBACK_LAYOUT_LANGUAGE = "fr";

/**
 * Site languages editor : the `languages` singleton (one document, stored under the "default"
 * language since it applies site-wide). Loads GET /api/content/languages/default, plus the layout
 * singleton (FR) solely to render the live header preview strip; saves via the same PUT as any
 * content and stages with POST /api/deployment/languages/default/deploy-preview. La carte de
 * production renvoie toujours a la page de Deploiement ; depuis le 01/09/2026, la modale qui suit
 * un enregistrement propose en plus la mise en production a qui detient le droit global de
 * deploiement, avec la meme confirmation qu'ailleurs. Fields are read-only until "Enable
 * editing" to guard a setting that affects every public page; the deployment cards derive
 * up-to-date / needs-deploy by comparing sanitized payload snapshots (current vs preview vs
 * production) rather than trusting timestamps.
 */
const Languages: React.FC = () => {
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [deployingProduction, setDeployingProduction] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [success, setSuccess] = useState<string | null>(null);
	const [content, setContent] = useState<LanguagesContent>(sanitizeContent());
	const [documentId, setDocumentId] = useState<string | null>(null);
	const [previewContent, setPreviewContent] = useState<LanguagesContent | null>(null);
	const [productionContent, setProductionContent] = useState<LanguagesContent | null>(null);
	const [previewDeployedAt, setPreviewDeployedAt] = useState<string | null>(null);
	const [productionDeployedAt, setProductionDeployedAt] = useState<string | null>(null);
	const [layoutPayload, setLayoutPayload] = useState<SiteSettingsModel | null>(null);
	const [isEditing, setIsEditing] = useState(false);
	const [uploadModalOpen, setUploadModalOpen] = useState(false);
	const [previewOpen, setPreviewOpen] = useState(false);

	const hasActiveLanguages = useMemo(() => content.languages.some((language) => !language.isDisabled), [content]);

	const currentComparable = useMemo(() => buildComparableFromContent(content), [content]);
	const previewComparable = useMemo(() => buildComparableFromContent(previewContent), [previewContent]);
	const productionComparable = useMemo(() => buildComparableFromContent(productionContent), [productionContent]);

	const previewStatus = useMemo<DeploymentStatus>(() => {
		if (!previewComparable) return { state: "pending", deployedAt: previewDeployedAt };
		if (!currentComparable) return { state: "pending", deployedAt: previewDeployedAt };
		return {
			state: areComparablesEqual(previewComparable, currentComparable) ? "up-to-date" : "needs-deploy",
			deployedAt: previewDeployedAt
		};
	}, [previewComparable, currentComparable, previewDeployedAt]);

	const productionStatus = useMemo<DeploymentStatus>(() => {
		if (!productionComparable) return { state: "pending", deployedAt: productionDeployedAt };
		if (!previewComparable) return { state: "needs-deploy", deployedAt: productionDeployedAt };
		return {
			state: areComparablesEqual(productionComparable, previewComparable) ? "up-to-date" : "needs-deploy",
			deployedAt: productionDeployedAt
		};
	}, [productionComparable, previewComparable, productionDeployedAt]);

	// The header preview should show what the deployed site shows (the preview payload) : except
	// mid-edit, when it mirrors the operator's unsaved draft so changes are visible immediately.
	const availableLanguages = useMemo(() => {
		const source = isEditing ? content.languages : previewContent?.languages?.length ? previewContent.languages : content.languages;
		return (source ?? [])
			.filter((l) => !l.isDisabled)
			.map((l) => ({
				code: (l.code ?? "").trim().toLowerCase(),
				label: (l.label || l.code || "").toUpperCase()
			}))
			.filter((l) => l.code.length > 0);
	}, [previewContent, content, isEditing]);

	const previewLocale = useMemo(() => {
		if (isEditing && content.defaultLanguageCode) return content.defaultLanguageCode.toLowerCase();
		if (!isEditing && previewContent?.defaultLanguageCode) return previewContent.defaultLanguageCode.toLowerCase();
		if (content.defaultLanguageCode) return content.defaultLanguageCode.toLowerCase();
		if (availableLanguages.length > 0) return availableLanguages[0].code;
		return FALLBACK_LAYOUT_LANGUAGE;
	}, [previewContent, content, availableLanguages, isEditing]);

	const headerPreview = useMemo(() => {
		if (!layoutPayload) return null;
		const payload = buildLayoutPayloadFromSettings(sanitizeLayoutSettingsLayout(layoutPayload));
		return buildHeaderData(payload?.header);
	}, [layoutPayload]);

	const applyViewModel = useCallback((view?: ContentViewModel | null) => {
		const current = sanitizeContent((view?.current?.data as LanguagesContent) ?? undefined);
		const preview = sanitizeContent((view?.preview?.payload as LanguagesContent) ?? undefined);
		const production = sanitizeContent((view?.production?.payload as LanguagesContent) ?? undefined);

		setContent(current);
		setDocumentId(view?.current?.id ?? null);
		setPreviewContent(preview.languages.length ? preview : null);
		setProductionContent(production.languages.length ? production : null);
		setPreviewDeployedAt(view?.preview?.deployedAt ?? null);
		setProductionDeployedAt(view?.production?.deployedAt ?? null);
		setIsEditing(false);
}, []);

	const load = useCallback(async () => {
		setLoading(true);
		setError(null);
		setSuccess(null);
		try {
			const response = await client.content.apiContentSchemaKeyLanguageGet(CONTENT_TYPE_KEY, CONTENT_LANGUAGE);
			applyViewModel(response.data ?? null);

			// Also fetch layout (FR) once for header preview strip.
			try {
				const layoutView = await client.content.apiContentSchemaKeyLanguageGet("layout", FALLBACK_LAYOUT_LANGUAGE);
				const layoutData =
					(layoutView.data?.preview?.payload as SiteSettingsModel) ??
					(layoutView.data?.production?.payload as SiteSettingsModel) ??
					(layoutView.data?.current?.data as SiteSettingsModel) ??
					null;
				setLayoutPayload(layoutData ? sanitizeLayoutSettingsLayout(layoutData) : null);
			} catch (layoutErr) {
				console.error("Failed to load layout for languages header preview", layoutErr);
				setLayoutPayload(null);
			}
		} catch (err) {
			console.error(err);
			setError("Unable to load languages. Please try again.");
		} finally {
			setLoading(false);
		}
	}, [applyViewModel]);

	useEffect(() => {
		load();
	}, [load]);

	const updateLanguage = (index: number, updater: (item: LanguageItem) => LanguageItem) => {
		setContent((prev) => {
			const languages = prev.languages.map((item, idx) => (idx === index ? updater(item) : item));
			return sanitizeContent({ ...prev, languages });
		});
	};

	const handleAddLanguage = () => {
		setContent((prev) => sanitizeContent({ ...prev, languages: [...prev.languages, createEmptyLanguage()] }));
	};

	const handleRemoveLanguage = (index: number) => {
		setContent((prev) => sanitizeContent({ ...prev, languages: prev.languages.filter((_, idx) => idx !== index) }));
	};

	const handleSave = useCallback(async () => {
		setSaving(true);
		setError(null);
		setSuccess(null);
		try {
			const payload: ContentDocument = {
				contentType: CONTENT_TYPE_KEY,
				language: CONTENT_LANGUAGE,
				data: sanitizeContent(content)
			};
			// slug query parameter is only required for page schema; languages does not use it.
			const response = await client.content.apiContentSchemaKeyLanguagePut(
				CONTENT_TYPE_KEY,
				CONTENT_LANGUAGE,
				undefined,
				payload
			);
			applyViewModel(response.data ?? null);
			const successMessage = "Languages saved successfully.";
			setSuccess(successMessage);
			// L'identifiant est lu sur la reponse : l'etat React n'est pas encore a jour ici.
			const savedId = response.data?.current?.id ?? null;
			showSuccessModal({
				title: "Languages saved",
				message: successMessage,
				// Pour qui detient le droit : enregistrer et mettre en production en un geste.
				publish: savedId
					? { contentId: savedId, contentType: CONTENT_TYPE_KEY, onPublished: () => void load() }
					: undefined
			});
		} catch (err) {
			console.error(err);
			setError("Unable to save languages. Please try again.");
		} finally {
			setSaving(false);
		}
	}, [content, client, applyViewModel, load]);

	const openUploadModal = useCallback(() => {
		setUploadModalOpen(true);
	}, []);

	const closeUploadModal = useCallback(() => {
		setUploadModalOpen(false);
	}, []);

	const handleApplyUploadedJson = useCallback((parsed: any) => {
		setContent(sanitizeContent(parsed as LanguagesContent));
		setSuccess("Languages JSON imported. Review and save.");
		setError(null);
		closeUploadModal();
	}, [closeUploadModal]);

	const headerActions = useMemo(
		() => (
			<div className="flex flex-wrap gap-2">
				<button
					type="button"
					onClick={() => setPreviewOpen(true)}
					disabled={loading}
					className="rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
				>
					<FiEye className="inline-block mr-2" /> Preview
				</button>
				<button
					type="button"
					onClick={openUploadModal}
					className="rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50"
				>
					<FiUpload className="inline-block mr-2" /> Upload JSON
				</button>
				{isEditing ? (
					<>
						<button
							type="button"
							onClick={handleSave}
							disabled={saving || !hasActiveLanguages}
							className="rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-400"
						>
							{saving ? "Saving…" : "Save"}
						</button>
						<button
							type="button"
							onClick={() => {
								setIsEditing(false);
								load();
							}}
							className="rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
						>
							Cancel
						</button>
					</>
				) : (
					<button
						type="button"
						onClick={() => setIsEditing(true)}
						className="rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
					>
						Enable editing
					</button>
				)}
			</div>
		),
		[
			loading,
			isEditing,
			saving,
			hasActiveLanguages,
			handleSave,
			load,
			openUploadModal,
			setIsEditing
		]
	);

	usePageHeader({
		title: "Languages",
		actions: headerActions
	});

	const deploymentCards = [
		{
			label: "Preview environment",
			status: previewStatus.state,
			deployedAt: previewStatus.deployedAt,
			action: (
				<span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400" title="La preview se met à jour automatiquement après chaque enregistrement">
					Mise à jour automatique
				</span>
			)
		},
		{
			label: "Production environment",
			status: productionStatus.state,
			deployedAt: productionStatus.deployedAt,
			action: (
				<Link
					to="/deployment"
					className="inline-flex items-center rounded-full border border-slate-200 bg-white/70 px-4 py-2 text-xs font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
				>
					Production
				</Link>
			)
		}
	];

	return (
		<>
			<div className="w-full h-full">
				<div className="px-4 sm:px-6 pt-4 pb-6 space-y-5">
					<div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
						<p className="text-sm text-slate-600">
							Manage locale availability and default language. Only enabled languages are deployed to the live sites.
						</p>
					</div>
					{error && (
						<div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
							{error}
						</div>
					)}
					{success && (
						<div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
							{success}
						</div>
					)}

					<DeploymentStatusPanel cards={deploymentCards} />

					{loading ? (
						<div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-12 text-center text-slate-500">
							Loading languages…
						</div>
					) : (
						<div className="grid layout-editor-grid gap-6 xl:gap-8">
							<div className="space-y-6">
								<div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm space-y-3">
									<label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Default language</label>
									<select
										className="w-64 rounded-lg border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-slate-400 focus:outline-none disabled:bg-slate-100"
										value={content.defaultLanguageCode}
										disabled={!isEditing}
										onChange={(event) =>
											setContent((prev) => sanitizeContent({ ...prev, defaultLanguageCode: event.target.value }))
										}
									>
										<option value="">Choose…</option>
										{content.languages
											.filter((language) => !language.isDisabled)
											.map((language) => (
												<option key={language.code} value={language.code}>
													{language.label || language.code}
												</option>
											))}
									</select>
								</div>

								<div className="grid grid-cols-1 gap-4">
									{content.languages.map((language, index) => (
										<div
											key={`${language.code}-${index}`}
											className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm space-y-3"
										>
											<div className="flex items-center justify-between gap-3 flex-wrap">
												<div className="flex items-center gap-3 flex-wrap">
													<div className="flex flex-col gap-1">
														<label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Code</label>
														<input
															type="text"
															className="w-28 rounded-lg border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-slate-400 focus:outline-none disabled:bg-slate-100"
															value={language.code ?? ""}
															disabled={!isEditing}
															onChange={(event) =>
																updateLanguage(index, (prev) => ({ ...prev, code: event.target.value }))
															}
														/>
													</div>
													<div className="flex flex-col gap-1">
														<label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Label</label>
														<input
															type="text"
															className="w-48 rounded-lg border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-slate-400 focus:outline-none disabled:bg-slate-100"
															value={language.label ?? ""}
															disabled={!isEditing}
															onChange={(event) =>
																updateLanguage(index, (prev) => ({ ...prev, label: event.target.value }))
															}
														/>
													</div>
													<div className="flex flex-col gap-1">
														<label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Flag ISO</label>
														<input
															type="text"
															className="w-24 rounded-lg border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-slate-400 focus:outline-none disabled:bg-slate-100"
															value={language.flagIso ?? ""}
															disabled={!isEditing}
															onChange={(event) =>
																updateLanguage(index, (prev) => ({ ...prev, flagIso: event.target.value }))
															}
														/>
													</div>
												</div>

												<div className="flex items-center gap-3">
													<label className="inline-flex items-center gap-2 text-sm font-semibold text-slate-700">
														<input
															type="checkbox"
															className="h-4 w-4 rounded border-slate-300 text-slate-900"
															checked={language.isDisabled ?? false}
															disabled={!isEditing}
															onChange={(event) =>
																updateLanguage(index, (prev) => ({ ...prev, isDisabled: event.target.checked }))
															}
														/>
														Disabled
													</label>
													{isEditing && content.languages.length > 1 && (
														<button
															type="button"
															onClick={() => handleRemoveLanguage(index)}
															className="rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-rose-600 transition hover:border-rose-300 hover:bg-rose-50"
														>
															Remove
														</button>
													)}
												</div>
											</div>
										</div>
									))}
								</div>

								{isEditing && (
									<button
										type="button"
										onClick={handleAddLanguage}
										className="rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-slate-400 hover:bg-slate-50"
									>
										Add language
									</button>
								)}
							</div>

							<aside className="w-full">
								<div className="sticky top-4 max-h-[calc(100vh-3rem)] space-y-3 overflow-y-auto pr-1">
									<div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-xs font-semibold uppercase tracking-[0.3em] text-slate-500">
										Header preview
									</div>
									{headerPreview ? (
										<>
											<div className="hidden show-1840-block">
												<LayoutPreviewStrip
													mode="header"
													headerData={headerPreview}
													language={previewLocale}
													languageOptions={availableLanguages}
													defaultLanguageCode={previewLocale}
													viewport="desktop"
													title="Header preview"
													description="Desktop view"
												/>
											</div>
											<LayoutPreviewStrip
												mode="header"
												headerData={headerPreview}
												language={previewLocale}
												languageOptions={availableLanguages}
												defaultLanguageCode={previewLocale}
												viewport="mobile"
												title="Header preview"
												description="Mobile view"
											/>
										</>
									) : (
										<div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-8 text-center text-slate-500">
											No header preview available. Make sure layout content is deployed.
										</div>
									)}
								</div>
							</aside>
						</div>
					)}
					{/* Historique du singleton languages : même panneau que /layout et /contact-info. */}
					{!loading && <ContentHistoryPanel contentId={documentId} />}
				</div>
			</div>

			<UploadJsonModal
				show={uploadModalOpen}
				onHide={closeUploadModal}
				onUpload={handleApplyUploadedJson}
				title="Import languages JSON"
				description="The JSON should follow the languages schema. Disabled languages remain stored but are not deployed."
			/>
			<LayoutPreviewModal
				show={previewOpen}
				onClose={() => setPreviewOpen(false)}
				initialLocale={previewLocale}
				localeOverride={previewLocale}
				languageOptionsOverride={availableLanguages}
			/>
		</>
	);
};

export default Languages;

